import { CartDiscountUpdateEvent, StandardEvents } from '@shopify/events';

const STORAGE_KEY = 'hoi-spin-wheel';
const SEGMENTS = [10, 15, 20, 10, 15, 20];
const SEGMENT_ANGLE = 360 / SEGMENTS.length;

/**
 * Persisted state, one per browser.
 * status: 'shown' (popup opened, not spun) | 'active' (code applied, timer running) | 'done'
 * @typedef {{ status: 'shown' | 'active' | 'done', percent?: number, expiresAt?: number }} SpinState
 */

/** @returns {SpinState | null} */
function readState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

/** @param {SpinState} state */
function writeState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (e) {}
}

/**
 * Reward tier for the cart: lowest qualifying tier wins when the cart is mixed.
 * Any 100ml in the cart means no reward (the 100ml BOGO offer applies instead).
 * @param {{ items: Array<{ product_type?: string, variant_options?: string[], variant_title?: string | null }> }} cart
 * @returns {number | null}
 */
function tierForCart(cart) {
  /** @type {number[]} */
  const tiers = [];

  for (const item of cart.items) {
    const type = (item.product_type || '').toLowerCase();
    const options = [...(item.variant_options || []), item.variant_title || ''].join(' ').toLowerCase();

    if (type === 'discovery set' || type === 'layering set') tiers.push(10);
    // 100ml carts get the automatic Buy One Get One Free discount instead of the wheel.
    else if (/\b100\s?ml\b/.test(options)) return null;
    else if (/\b(30|50)\s?ml\b/.test(options)) tiers.push(15);
  }

  return tiers.length ? Math.min(...tiers) : null;
}

/** @param {number} ms */
function formatTime(ms) {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = String(total % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
}

class SpinWheelPopup extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  dialog = null;
  /** @type {number | undefined} */
  #ticker;
  #busy = false;

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.codes = {
      // getAttribute rather than dataset: `data-code-10` doesn't camelCase to `code10`.
      10: this.getAttribute('data-code-10') || '',
      15: this.getAttribute('data-code-15') || '',
      20: this.getAttribute('data-code-20') || '',
    };
    this.minutes = Number(this.dataset.minutes) || 8;

    this.#drawWheel();

    this.querySelectorAll('[data-spin-close]').forEach((button) =>
      button.addEventListener('click', () => this.dialog?.close())
    );
    this.querySelector('[data-spin-button]')?.addEventListener('click', this.#spin);
    this.dialog?.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);

    this.#resume();
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
    window.clearInterval(this.#ticker);
  }

  /** Restores the pill/timer on page load, or cleans up an expired code. */
  #resume() {
    const state = readState();
    if (state?.status !== 'active' || !state.expiresAt) return;

    if (Date.now() >= state.expiresAt) {
      this.#expire();
    } else {
      this.#startTicker();
    }
  }

  /** @param {Event} event */
  #onCartLinesUpdate = async (event) => {
    const state = readState();
    if (state?.status === 'done') return;

    const { action, promise } = /** @type {any} */ (event);
    try {
      await promise;
    } catch (e) {
      return;
    }

    if (!state) {
      if (action !== 'add') return;
      const cart = await this.#fetchCart();
      if (!cart || !tierForCart(cart)) return;
      // Let the cart drawer / fly-to-cart animation settle first.
      window.setTimeout(() => this.#open(), 900);
      return;
    }

    // While the timer runs, keep the applied code in line with the cart contents.
    if (state.status === 'active' && state.expiresAt && Date.now() < state.expiresAt) {
      const cart = await this.#fetchCart();
      if (!cart) return;
      const tier = tierForCart(cart);
      await this.#setCode(tier ? this.codes[/** @type {10 | 15 | 20} */ (tier)] : null, cart);
      if (tier && tier !== state.percent) {
        writeState({ ...state, percent: tier });
        this.#renderPill();
      }
    }
  };

  #open() {
    if (!this.dialog || this.dialog.open || readState()) return;
    writeState({ status: 'shown' });
    this.dialog.showModal();
  }

  #spin = async () => {
    if (this.#busy) return;
    this.#busy = true;

    const spinButton = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-spin-button]'));
    if (spinButton) spinButton.disabled = true;

    const cart = await this.#fetchCart();
    const tier = cart ? tierForCart(cart) : null;
    if (!cart || !tier) {
      this.#showMessage('Add a fragrance to your cart to claim your reward.');
      writeState({ status: 'done' });
      return;
    }

    const expiresAt = Date.now() + this.minutes * 60 * 1000;
    writeState({ status: 'active', percent: tier, expiresAt });
    const applied = this.#setCode(this.codes[/** @type {10 | 15 | 20} */ (tier)], cart);

    await this.#animateTo(tier);
    await applied;

    const heading = this.querySelector('[data-spin-heading]');
    const text = this.querySelector('[data-spin-text]');
    if (heading) heading.textContent = `You've won ${tier}% off your whole order`;
    if (text) text.textContent = `It's already applied to your cart — but only for the next ${this.minutes} minutes.`;

    const checkout = /** @type {HTMLAnchorElement | null} */ (this.querySelector('[data-spin-checkout]'));
    if (checkout) checkout.href = this.dataset.checkoutUrl || '/checkout';

    spinButton?.setAttribute('hidden', '');
    this.querySelector('[data-spin-result]')?.removeAttribute('hidden');
    this.dialog?.classList.add('is-won');
    this.#startTicker();
  };

  /**
   * Rotates the wheel so a segment with the given value stops under the pointer.
   * @param {number} tier
   */
  #animateTo(tier) {
    const wheel = this.querySelector('[data-spin-wheel]');
    if (!(wheel instanceof SVGElement)) return Promise.resolve();

    const matches = SEGMENTS.map((value, index) => (value === tier ? index : -1)).filter((i) => i >= 0);
    const index = matches[Math.floor(Math.random() * matches.length)] ?? 0;
    const jitter = (Math.random() - 0.5) * (SEGMENT_ANGLE * 0.6);
    const rotation = 360 * 6 + (360 - index * SEGMENT_ANGLE) + jitter;

    return new Promise((resolve) => {
      const done = () => resolve(undefined);
      wheel.addEventListener('transitionend', done, { once: true });
      window.setTimeout(done, 5000);
      wheel.classList.add('is-spinning');
      requestAnimationFrame(() => {
        wheel.style.transform = `rotate(${rotation}deg)`;
      });
    });
  }

  #startTicker() {
    window.clearInterval(this.#ticker);
    this.#renderPill();
    this.#tick();
    this.#ticker = window.setInterval(() => this.#tick(), 1000);
  }

  #tick() {
    const state = readState();
    if (state?.status !== 'active' || !state.expiresAt) {
      window.clearInterval(this.#ticker);
      return;
    }

    const remaining = state.expiresAt - Date.now();
    const label = formatTime(remaining);
    const countdown = this.querySelector('[data-spin-countdown]');
    const pillTime = this.querySelector('[data-spin-pill-time]');
    if (countdown) countdown.textContent = label;
    if (pillTime) pillTime.textContent = label;

    const urgent = remaining <= 60 * 1000;
    this.querySelector('.spin-wheel__timer')?.classList.toggle('is-urgent', urgent);
    this.querySelector('[data-spin-pill]')?.classList.toggle('is-urgent', urgent);

    if (remaining <= 0) this.#expire();
  }

  #renderPill() {
    const state = readState();
    const pill = this.querySelector('[data-spin-pill]');
    const label = this.querySelector('[data-spin-pill-label]');
    if (!pill || !label || state?.status !== 'active') return;
    label.textContent = `Your ${state.percent}% off ends in`;
    pill.removeAttribute('hidden');
  }

  async #expire() {
    window.clearInterval(this.#ticker);
    writeState({ status: 'done' });

    const pill = this.querySelector('[data-spin-pill]');
    pill?.setAttribute('hidden', '');

    if (this.dialog?.open) {
      this.#showMessage("Time's up — your reward has expired.");
    }

    const cart = await this.#fetchCart();
    if (cart) await this.#setCode(null, cart);
  }

  /** @param {string} message */
  #showMessage(message) {
    const heading = this.querySelector('[data-spin-heading]');
    const text = this.querySelector('[data-spin-text]');
    if (heading) heading.textContent = message;
    if (text) text.textContent = '';
    this.querySelector('[data-spin-button]')?.setAttribute('hidden', '');
    this.querySelector('[data-spin-result]')?.setAttribute('hidden', '');
  }

  /** @returns {Promise<any | null>} */
  async #fetchCart() {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      return response.ok ? await response.json() : null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Replaces any spin code on the cart with `code` (or removes it when null), keeping
   * other codes the customer has applied. Dispatches a discount update so the cart UI refreshes.
   * @param {string | null} code
   * @param {any} cart
   */
  async #setCode(code, cart) {
    const spinCodes = Object.values(this.codes).map((c) => c.toLowerCase());
    /** @type {string[]} */
    const existing = cart.discount_codes
      ? cart.discount_codes.map((/** @type {{ code: string }} */ d) => d.code)
      : (cart.cart_level_discount_applications || [])
          .filter((/** @type {{ type: string }} */ d) => d.type === 'discount_code')
          .map((/** @type {{ title: string }} */ d) => d.title);
    const others = existing.filter((c) => !spinCodes.includes(c.toLowerCase()));
    const next = code ? [...others, code] : others;

    const unchanged =
      next.length === existing.length && next.every((c) => existing.some((e) => e.toLowerCase() === c.toLowerCase()));
    if (unchanged) return;

    const deferred = CartDiscountUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartDiscountUpdateEvent({
        discountCodes: next.map((c) => ({ code: c })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_update_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: next.join(',') }),
      });
      const data = await response.json();
      deferred.resolve({ cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data) });
    } catch (error) {
      deferred.reject(error);
    }
  }

  #drawWheel() {
    const svg = this.querySelector('[data-spin-wheel]');
    if (!svg) return;

    const ns = 'http://www.w3.org/2000/svg';
    const cx = 100;
    const cy = 100;
    const r = 96;
    /** @param {number} deg */
    const point = (deg) => {
      const rad = (deg * Math.PI) / 180;
      return `${cx + r * Math.sin(rad)} ${cy - r * Math.cos(rad)}`;
    };

    SEGMENTS.forEach((value, index) => {
      const start = index * SEGMENT_ANGLE - SEGMENT_ANGLE / 2;
      const end = start + SEGMENT_ANGLE;
      const dark = index % 2 === 0;

      const slice = document.createElementNS(ns, 'path');
      slice.setAttribute('d', `M ${cx} ${cy} L ${point(start)} A ${r} ${r} 0 0 1 ${point(end)} Z`);
      slice.setAttribute('fill', dark ? 'var(--color-foreground)' : 'var(--color-background)');
      slice.setAttribute('stroke', 'var(--color-foreground)');
      slice.setAttribute('stroke-width', '1.5');
      svg.appendChild(slice);

      const label = document.createElementNS(ns, 'text');
      label.setAttribute('x', String(cx));
      label.setAttribute('y', '44');
      label.setAttribute('text-anchor', 'middle');
      label.setAttribute('fill', dark ? 'var(--color-background)' : 'var(--color-foreground)');
      label.setAttribute('transform', `rotate(${index * SEGMENT_ANGLE} ${cx} ${cy})`);
      label.textContent = `${value}%`;
      svg.appendChild(label);
    });

    const hub = document.createElementNS(ns, 'circle');
    hub.setAttribute('cx', String(cx));
    hub.setAttribute('cy', String(cy));
    hub.setAttribute('r', '14');
    hub.setAttribute('fill', 'var(--color-background)');
    hub.setAttribute('stroke', 'var(--color-foreground)');
    hub.setAttribute('stroke-width', '2');
    svg.appendChild(hub);
  }
}

if (!customElements.get('spin-wheel-popup')) {
  customElements.define('spin-wheel-popup', SpinWheelPopup);
}
